/* eslint-disable */
import * as yup from "yup";
import { padStart } from "lodash";
import { CalendarToday as CalendarTodayIcon, Schedule as ScheduleIcon } from "@material-ui/icons";

export type FieldType = "append_note" | "date_with_format" | "time_with_format";

interface Field {
  format?: FieldType;
  label: string;
  schema?: yup.BaseSchema;
  icon?: React.ElementType;
}

interface Fields {
  [name: string]: Field;
}

const s = yup
  .string()
  .trim()
  .transform(v => v || undefined);


const numeric = s
  .transform(value => {
    if (typeof value !== "string") {
      return undefined;
    }

    const numbers = value.replace(/[^0-9]/g, "");
    if (numbers === "") {
      return undefined;
    }

    return numbers;
  })
  .matches(/^\d+$/, { excludeEmptyString: true });

const boolean = s
  .lowercase()
  .transform(value => {
    if (typeof value !== "string") {
      return value;
    }

    switch (value) {
      case "true":
        return "true";
      case "false":
        return "false";
      case "yes":
        return "true";
      case "no":
        return "false";
      case "y":
        return "true";
      case "n":
        return "false";
      case "x":
        return "true";
      default:
        return value;
    }
  })
  .oneOf(["true", "false"]);

const zip = s
  .transform(value => (value ? padStart(value, 5, "0") : value))
  .test("is-zip", "ZIP must be of the form 12345 or 12345-0987.", value =>
    (typeof value === "string" ? value.length === 5 || value.length === 10 : true));

const phoneNumber = numeric.min(10).max(11);
const phoneType = s
  .lowercase()
  .transform(value => {
    switch (value) {
      case "home":
        return "landline";
      default:
        return value;
    }
  })
  .oneOf(["landline", "mobile"], "Phone types include 'landline' and 'mobile'.");

const unitSizeOptions = ['sro', 'shared', 'studio', '1br', '1br_den', '2br', '3br', '4br', '5br'];

const unitSize = s
  .lowercase()
  .test("is-unit-size-array", "Must be a comma separated list of valid unit sizes (1br, 2br, sro, etc.)", value =>
    (value?.split(",")
        .map(unit => unit.trim())
        .every(unit => unitSizeOptions.includes(unit)) ?? true
    ));

export const fields: Fields = {
  none: {
    label: "Skip",
  },
  add_notes: {
    label: "Add to notes",
    format: "append_note",
  },
  date: {
    label: "Submit date",
    icon: CalendarTodayIcon,
  },
  time: {
    label: "Submit time",
    icon: ScheduleIcon,
  },
  position_number: {
    label: "Position",
    schema: numeric.required(),
  },
  household_count: {
    label: "Household size",
    schema: numeric.default("1"),
  },
  household_member_0_first_name: {
    label: "Applicant first name",
  },
  household_member_0_middle_name: {
    label: "Applicant middle name",
  },
  household_member_0_last_name: {
    label: "Applicant last name",
  },
  household_member_0_email: {
    label: "Applicant email",
  },
  household_member_0_primary_phone: {
    label: "Applicant primary phone number",
    schema: phoneNumber,
  },
  household_member_0_primary_phone_type: {
    label: "Applicant primary phone type",
    schema: phoneType,
  },
  household_member_0_secondary_phone: {
    label: "Applicant secondary phone number",
    schema: phoneNumber,
  },
  household_member_0_secondary_phone_ext: {
    label: "Applicant secondary phone extension",
    schema: numeric,
  },
  household_member_0_secondary_phone_type: {
    label: "Applicant secondary phone type",
    schema: phoneType,
  },
  household_member_0_dob: {
    label: "Applicant date of birth",
  },
  residence_address1: {
    label: "Residence address 1",
  },
  residence_ste_number: {
    label: "Residence address 2",
  },
  residence_city: {
    label: "Residence city",
  },
  residence_state: {
    label: "Residence state",
    schema: s.length(2),
  },
  residence_zip: {
    label: "Residence ZIP",
    schema: zip,
  },
  mailing_address1: {
    label: "Mailing address 1",
  },
  mailing_ste_number: {
    label: "Mailing address 2",
  },
  mailing_city: {
    label: "Mailing city",
  },
  mailing_state: {
    label: "Mailing state",
    schema: s.length(2),
  },
  mailing_zip: {
    label: "Mailing ZIP",
    schema: zip,
  },
  total_income: {
    label: "Applicant total gross income",
  },
  household_ami: {
    label: "Household AMI",
    schema: s,
  },
  unit_size: {
    label: "Unit Size",
    schema: unitSize,
  },
  unit_size_studio: {
    label: "Unit size: Studio?",
    schema: boolean,
  },
  unit_size_1br: {
    label: "Unit size: 1BR?",
    schema: boolean,
  },
  unit_size_1br_den: {
    label: "Unit size: 1BR + Den?",
    schema: boolean,
  },
  unit_size_2br: {
    label: "Unit size: 2BR?",
    schema: boolean,
  },
  unit_size_3br: {
    label: "Unit size: 3BR?",
    schema: boolean,
  },
  unit_size_4br: {
    label: "Unit size: 4BR?",
    schema: boolean,
  },
  unit_size_5br: {
    label: "Unit size: 5BR?",
    schema: boolean,
  },
  unit_size_sro: {
    label: "Unit size: SRO?",
    schema: boolean,
  },
  unit_size_shared: {
    label: "Unit size: Shared?",
    schema: boolean,
  },
  household_has_housing_authority_voucher: {
    label: "Has housing authority voucher?",
    schema: boolean,
  },
  household_receives_other_housing_assistance: {
    label: "Receives other housing assistance?",
    schema: boolean,
  },
  household_receives_other_housing_assistance_subsidy_type: {
    label: "Other housing assistance type",
  },
  household_requires_impairment_mobility: {
    label: "Household requires mobility impairment accessibility?",
    schema: boolean,
  },
  household_requires_impairment_visual: {
    label: "Household requires visual impairment accessibility?",
    schema: boolean,
  },
  household_requires_impairment_hearing: {
    label: "Household required hearing impairment accessibility?",
    schema: boolean,
  },
  household_served_in_armed_forces: {
    label: "Served in armed forces?",
    schema: boolean,
  },
  household_member_1_first_name: {
    label: "Secondary applicant first name",
  },
  household_member_1_last_name: {
    label: "Secondary applicant last name",
  },
  current_resident: {
    label: "Current resident?",
    schema: boolean,
  },
  denied: {
    label: "Removed",
    schema: boolean,
  },
  denial_reason: {
    label: "Removal reason",
  },
  moved_in: {
    label: "Moved In",
    schema: boolean,
  },
  movein_unit_no: {
    label: "Moved-in unit number",
    schema: numeric,
  },
  custom_pref_1: {
    label: "Custom preference 1",
  },
  custom_pref_2: {
    label: "Custom preference 2",
  },
  custom_pref_3: {
    label: "Custom preference 3",
  },
  custom_pref_4: {
    label: "Custom preference 4",
  },
  custom_pref_5: {
    label: "Custom preference 5",
  },
  custom_pref_6: {
    label: "Custom preference 6",
  },
  custom_pref_7: {
    label: "Custom preference 7",
  },
  custom_pref_8: {
    label: "Custom preference 8",
  },
  custom_pref_9: {
    label: "Custom preference 9",
  },
  custom_pref_10: {
    label: "Custom preference 10",
  },
  contact_name: {
    label: "Optional contact name",
  },
  contact_relation: {
    label: "Optional contact relationship",
  },
  contact_primary_phone: {
    label: "Optional contact phone number",
    schema: phoneNumber,
  },
  contact_primary_phone_ext: {
    label: "Optional contact phone number extension",
    schema: numeric,
  },
  contact_email: {
    label: "Optional contact email address",
  },
  applicant_prefered_contact_type: {
    label: "Preferred contact type",
    schema: s.lowercase().oneOf(["email", "fax", "mail", "mobile", "phone", "unknown", "walk-in"]),
  },
  applicant_homeless_or_no_residence: {
    label: "Homeless?",
    schema: boolean,
  },
  household_has_pets: {
    label: "Household has pets?",
    schema: boolean,
  },
  household_all_full_time_students: {
    label: "Are all household members full-time students?",
    schema: boolean,
  },
  household_has_special_accommodations: {
    label: "Special Accommodations",
    schema: boolean,
  },
  how_did_applicant_hear_about_property: {
    label: "How did applicant hear about property",
  },
  skip: {
    label: "Skips",
    schema: numeric,
  },
  household_member_0_social: {
    label: "Applicant Social",
  },
  household_member_0_ethnic_category: {
    label: "Ethnic Category",
  },
  household_member_0_race_amer_indian_alaskan_native: {
    label: "Race: American Indian or Alaskan Native",
    schema: boolean,
  },
  household_member_0_race_asian: {
    label: "Race: Asian",
    schema: boolean,
  },
  household_member_0_race_african_american: {
    label: "Race: Black or African American",
    schema: boolean,
  },
  household_member_0_race_native_hawaiian_or_other_pac_islander: {
    label: "Race: Native Hawaiian or other Pacific Islander",
    schema: boolean,
  },
  household_member_0_race_white: {
    label: "Race: White",
    schema: boolean,
  },
  household_member_0_race_other: {
    label: "Race: Other",
    schema: boolean,
  },
  contact_reason_emergency: {
    label: "Contact Reason: Emergency",
  },
  contact_reason_unable_to_contact: {
    label: "Contact Reason: Unable to contact",
  },
  contact_reason_termination_of_rental_assist: {
    label: "Contact Reason: Termination of rental assist",
  },
  contact_reason_eviction_from_unit: {
    label: "Contact Reason: Eviction",
  },
  contact_reason_late_payment_of_rent: {
    label: "Contact Reason: Late payment",
  },
  household_member_0_gender: {
    label: "Applicant Gender",
  }
};
